import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader, LoadingPage, ErrorPage } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Input, Select, Checkbox } from '../../components/ui/Form.jsx';

// Oct 2026 — operator vehicles (fleet_partner_id set) only need the
// registration. Make, model and registered keeper are required for Lanz CTS
// vehicles only (refine below). Backend mirrors this in vehicleSchemas.js.
const schema = z.object({
    // Identity
       registration:               z.string().trim().min(1, 'Registration is required').max(15),
    make:                       z.string().trim().max(60).optional().or(z.literal('')),
    model:                      z.string().trim().max(60).optional().or(z.literal('')),
    year:                       z.string().optional().or(z.literal('')),
    colour:                     z.string().trim().max(40).optional().or(z.literal('')),

    // Classification
    vehicle_class:              z.enum(['business', 'first', 'suv', 'xl'], {
                                    errorMap: () => ({ message: 'Select a vehicle class' }),
                                }),
    // Stored as 'nickname' in DB — surfaced as 'Vehicle description' in UI.
    // This text is the customer-facing vehicle label used in manual booking
    // confirmations, e.g. "Black BMW 7-Series".
    nickname:                   z.string().trim().max(60).optional().or(z.literal('')),

    // Capacity
    seats:                      z.string().min(1, 'Seats is required'),
    luggage_capacity:           z.string().optional().or(z.literal('')),

    // Licensing
    licence_plate_number:       z.string().trim().max(30).optional().or(z.literal('')),
    licensing_authority:        z.string().trim().min(1, 'Licensing authority is required').max(80),

    // Registered keeper — required for own vehicles only (refine below)
    registered_keeper_name:     z.string().trim().max(120).optional().or(z.literal('')),
    registered_keeper_email:    z.string().trim().email('Enter a valid email').optional().or(z.literal('')),
    registered_keeper_phone:    z.string().trim().max(30).optional().or(z.literal('')),
    registered_keeper_address:  z.string().trim().max(300).optional().or(z.literal('')),

    // Flags
    notify_keeper_on_expiry:    z.boolean(),
    include_in_compliance:      z.boolean(),

    // Operator — set = belongs to an operator (their compliance), '' = own vehicle
    fleet_partner_id:           z.string().optional().or(z.literal('')),

    start_date: z.string().min(1, 'Start date is required'),
    end_date: z.string().optional().or(z.literal('')),
}).superRefine((data, ctx) => {
    if (data.fleet_partner_id) return; // operator vehicle — registration only
    if (!data.make) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['make'], message: 'Make is required' });
    if (!data.model) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['model'], message: 'Model is required' });
    if (!data.registered_keeper_name) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['registered_keeper_name'], message: 'Registered keeper name is required' });
    }
    if (!data.registered_keeper_email) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['registered_keeper_email'], message: 'Registered keeper email is required' });
    }
});

export function VehicleFormPage() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    // Opened from an operator's page ("Add vehicle") → preselect that operator
    // and go back to the operator's page afterwards.
    const [searchParams] = useSearchParams();
    const presetOperatorId = !isEdit ? (searchParams.get('operator') || '') : '';
    const api = useApi();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isAdmin } = useCurrentUser();

    const { data: existing, isLoading: isLoadingExisting, isError, error } = useQuery({
        queryKey: ['vehicle', id],
        queryFn: () => api.get(`/vehicles/${id}`),
        enabled: isEdit,
    });

    const { data: fleetPartnersData } = useQuery({
        queryKey: ['fleet-partners'],
        queryFn: () => api.get('/drivers/meta/fleet-partners'),
    });
    const fleetPartners = fleetPartnersData?.fleet_partners || [];

    const {
        register,
        handleSubmit,
        reset,
        setError,
        watch,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            registration: '',
            make: '',
            model: '',
            year: '',
            colour: '',
            vehicle_class: 'business',
            nickname: '',
            seats: '4',
            luggage_capacity: '',
            licence_plate_number: '',
            licensing_authority: 'TFL',
            registered_keeper_name: '',
            registered_keeper_email: '',
            registered_keeper_phone: '',
            registered_keeper_address: '',
            notify_keeper_on_expiry: true,
            include_in_compliance: true,
            fleet_partner_id: presetOperatorId,
            start_date: new Date().toISOString().slice(0, 10),
            end_date: '',
        },
    });

    useEffect(() => {
        if (existing) {
            reset({
                registration: existing.registration || '',
                make: existing.make || '',
                model: existing.model || '',
                year: existing.year ? String(existing.year) : '',
                colour: existing.colour || '',
                vehicle_class: existing.vehicle_class || 'business',
                nickname: existing.nickname || '',
                seats: existing.seats != null ? String(existing.seats) : '4',
                luggage_capacity: existing.luggage_capacity != null ? String(existing.luggage_capacity) : '',
                licence_plate_number: existing.licence_plate_number || '',
                licensing_authority: existing.licensing_authority || 'TFL',
                registered_keeper_name: existing.registered_keeper_name || '',
                registered_keeper_email: existing.registered_keeper_email || '',
                registered_keeper_phone: existing.registered_keeper_phone || '',
                registered_keeper_address: existing.registered_keeper_address || '',
                notify_keeper_on_expiry: existing.notify_keeper_on_expiry ?? true,
                include_in_compliance: existing.include_in_compliance ?? true,
                fleet_partner_id: existing.fleet_partner_id || '',
                start_date: existing.start_date?.slice(0, 10) || '',
                end_date: existing.end_date?.slice(0, 10) || '',
            });
        }
    }, [existing, reset]);

    const mutation = useMutation({
        mutationFn: async (data) => {
            const payload = {
                ...data,
                year: data.year ? parseInt(data.year, 10) : null,
                seats: parseInt(data.seats, 10),
                luggage_capacity: data.luggage_capacity ? parseInt(data.luggage_capacity, 10) : null,
            };

            // Operator vehicle with no keeper email → nobody to send expiry
            // reminders to, so switch them off.
            if (data.fleet_partner_id && !data.registered_keeper_email) {
                payload.notify_keeper_on_expiry = false;
            }

            const clean = {};
            for (const [k, v] of Object.entries(payload)) {
                if (v === undefined) continue;
                // On create, skip empty fields. On edit, send '' so the backend
                // clears the column (it converts '' to NULL).
                if (v === '' && !isEdit) continue;
                clean[k] = v;
            }
            // fleet_partner_id must go through even when clearing to null on edit
            if (isEdit && data.fleet_partner_id === '') {
                clean.fleet_partner_id = null;
            }

            if (isEdit) {
                return api.patch(`/vehicles/${id}`, clean);
            } else {
                return api.post('/vehicles', clean);
            }
        },
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['vehicles'] });
            if (isEdit) {
                queryClient.invalidateQueries({ queryKey: ['vehicle', id] });
                toast.success('Vehicle updated');
                navigate(`/vehicles/${id}`);
            } else {
                toast.success('Vehicle created');
                navigate(presetOperatorId ? `/chauffeurs/${presetOperatorId}` : `/vehicles/${result.id}`);
            }
        },
        onError: (err) => {
            if (err.fieldErrors) {
                for (const [field, messages] of Object.entries(err.fieldErrors)) {
                    setError(field, { message: Array.isArray(messages) ? messages[0] : messages });
                }
                toast.error('Please fix the errors below');
            } else {
                toast.error(err.message || 'Failed to save');
            }
        },
    });

    if (!isAdmin) {
        return <ErrorPage message="You don't have permission to edit vehicles." />;
    }

    if (isEdit && isLoadingExisting) return <LoadingPage />;
    if (isEdit && isError) return <ErrorPage message={error?.message || 'Could not load vehicle'} />;

    const title = isEdit
        ? `Edit ${existing?.nickname || `${existing?.make || ''} ${existing?.model || ''}`.trim() || existing?.registration || 'vehicle'}`
        : 'New vehicle';

    const partnerSelected = Boolean(watch('fleet_partner_id'));

    return (
        <div className="max-w-2xl">
            <PageHeader
                title={title}
                subtitle={isEdit ? 'Update vehicle details and reporting settings.' : 'Add a new vehicle to the fleet.'}
                backTo={isEdit ? `/vehicles/${id}` : (presetOperatorId ? `/chauffeurs/${presetOperatorId}` : '/vehicles')}
                backLabel={isEdit ? 'Back to vehicle' : (presetOperatorId ? 'Back to operator' : 'Back to vehicles')}
            />

            <form onSubmit={handleSubmit((data) => mutation.mutate(data))} noValidate>
                <div className="space-y-6">
                    <FormSection
                        title="Operator"
                        subtitle="Select an operator only if this vehicle runs under their operator licence — their compliance, excluded from Lanz CTS reports. Operator vehicles only need a registration. Leave unset for Lanz CTS vehicles."
                    >
                        <Select
                            // Re-mount once the operator list loads so a preselected
                            // operator (Add vehicle from an operator's page) shows.
                            key={fleetPartners.length}
                            label="Operator"
                            {...register('fleet_partner_id')}
                            error={errors.fleet_partner_id?.message}
                        >
                            <option value="">Lanz CTS vehicle (no operator)</option>
                            {fleetPartners.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.fleet_partner_company_name} ({p.first_name} {p.last_name})
                                </option>
                            ))}
                        </Select>
                    </FormSection>

                    <FormSection title="Identity">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input
                                label="Registration"
                                required
                                {...register('registration')}
                                error={errors.registration?.message}
                                hint="UK plate (case will be normalised)"
                                className="uppercase"
                            />
                            <Input
                                label="Vehicle description"
                                {...register('nickname')}
                                error={errors.nickname?.message}
                                hint="Used in manual booking confirmations (e.g. 'Black BMW 7-Series')"
                            />
                            <Input label="Make" required={!partnerSelected} {...register('make')} error={errors.make?.message} />
                            <Input label="Model" required={!partnerSelected} {...register('model')} error={errors.model?.message} />
                            <Input
                                label="Year"
                                type="number"
                                {...register('year')}
                                error={errors.year?.message}
                                hint="Optional — V5 logbook is source of truth"
                                min="1990"
                                max={new Date().getFullYear() + 1}
                            />
                            <Input
                                label="Colour"
                                {...register('colour')}
                                error={errors.colour?.message}
                            />
                        </div>
                    </FormSection>

                    <FormSection title="Classification & capacity">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Select label="Vehicle class" required {...register('vehicle_class')} error={errors.vehicle_class?.message}>
                                <option value="business">Business (Mercedes E-Class)</option>
                                <option value="first">First (S-Class / 7-Series)</option>
                                <option value="suv">SUV (Range Rover)</option>
                                <option value="xl">XL (V-Class)</option>
                            </Select>
                            <div />
                            <Input
                                label="Passenger seats"
                                type="number"
                                required
                                {...register('seats')}
                                error={errors.seats?.message}
                                min="1"
                                max="16"
                            />
                            <Input
                                label="Luggage capacity"
                                type="number"
                                {...register('luggage_capacity')}
                                error={errors.luggage_capacity?.message}
                                hint="Rough guide — suitcase count"
                                min="0"
                                max="20"
                            />
                        </div>
                    </FormSection>

                    <FormSection title="Engagement">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input label="Start date" type="date" required {...register('start_date')} error={errors.start_date?.message} />
                            <Input label="End date" type="date" {...register('end_date')} error={errors.end_date?.message} hint="Leave blank if currently in fleet" />
                        </div>
                    </FormSection>

                    <FormSection title="Licensing">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Select label="Licensing authority" required {...register('licensing_authority')} error={errors.licensing_authority?.message}>
                                <option value="TFL">TFL</option>
                                <option value="Castle Point">Castle Point</option>
                                <option value="Wolverhampton">Wolverhampton</option>
                                <option value="Other">Other</option>
                            </Select>
                            <Input
                                label="PHV licence number"
                                {...register('licence_plate_number')}
                                error={errors.licence_plate_number?.message}
                                hint="Authority-issued licence number"
                            />
                        </div>
                        <div className="mt-4">
                            <Checkbox
                                label="Include in reports"
                                description="Untick to exclude this vehicle from licensing authority reports (e.g. temporarily off-fleet, under review)."
                                {...register('include_in_compliance')}
                                error={errors.include_in_compliance?.message}
                            />
                        </div>
                    </FormSection>

                    <FormSection
                        title="Registered keeper"
                        subtitle={partnerSelected
                            ? 'Optional for operator vehicles. Leave blank if you don’t need expiry reminders for this vehicle.'
                            : 'The person responsible for renewing this vehicle’s documents. Often different from the driver — could be a leasing company, owner-investor, or company secretary.'}
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input
                                label="Keeper name"
                                required={!partnerSelected}
                                {...register('registered_keeper_name')}
                                error={errors.registered_keeper_name?.message}
                            />
                            <Input
                                label="Keeper email"
                                type="email"
                                required={!partnerSelected}
                                {...register('registered_keeper_email')}
                                error={errors.registered_keeper_email?.message}
                                hint="Receives doc expiry reminders"
                            />
                            <Input
                                label="Keeper phone"
                                type="tel"
                                {...register('registered_keeper_phone')}
                                error={errors.registered_keeper_phone?.message}
                            />
                            <Input
                                label="Keeper address"
                                {...register('registered_keeper_address')}
                                error={errors.registered_keeper_address?.message}
                            />
                        </div>
                        <div className="mt-4">
                            <Checkbox
                                label="Send expiry reminders to registered keeper"
                                description="Automatic emails when MOT, insurance, or PHV licence is approaching expiry."
                                {...register('notify_keeper_on_expiry')}
                                error={errors.notify_keeper_on_expiry?.message}
                            />
                        </div>
                    </FormSection>
                </div>

                <div className="mt-8 flex items-center justify-end gap-3 pt-6 border-t border-paper-border-soft">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => navigate(isEdit ? `/vehicles/${id}` : (presetOperatorId ? `/chauffeurs/${presetOperatorId}` : '/vehicles'))}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" loading={isSubmitting || mutation.isPending} disabled={isEdit && !isDirty}>
                        <Save className="h-4 w-4" />
                        {isEdit ? 'Save changes' : 'Create vehicle'}
                    </Button>
                </div>
            </form>
        </div>
    );
}

function FormSection({ title, subtitle, children }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">{title}</h2>
                {subtitle && <p className="mt-1 text-xs text-ink-subtle">{subtitle}</p>}
            </div>
            <div className="card-body">{children}</div>
        </div>
    );
}
